/**
 * A2 订单详情页（含替支付反悔退款）
 *
 * Stream 2 Phase 2-1b / Plan §10 Stream 2 / Design §5.1 A2 详情.
 *
 * 功能：
 *  - 基础信息 / 状态时间线 / 收件人 / 物流 / 支付 / 关联作品 区块
 *  - 替支付反悔模块（仅 isSubstitutePayment=true 时显示）
 *  - 管理员备注展示 + 编辑
 *  - 操作面板：录入运单 / 修改地址 / 取消订单（仅 pending_payment）/ 编辑备注
 *  - 所有写操作按 PermissionGate 权限点控制
 *
 * 权限主要点：
 *   order:read — 整页访问（由路由层 RequireAuth 保护，列表页已限制入口）
 *   order:update_logistics — 录入运单 / 修改地址 / 取消订单 / 编辑备注
 *   order:revert_substitute — 替支付反悔退款
 */
import { useState } from 'react';
import {
  Card,
  Descriptions,
  Timeline,
  Tag,
  Button,
  Space,
  Modal,
  Form,
  Input,
  InputNumber,
  message,
  Result,
  Spin,
  Typography,
  Alert,
} from 'antd';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PermissionGate } from '../../../components/PermissionGate';
import { InitiateRefundModal } from '../../../components/InitiateRefundModal';
import { PERMISSIONS } from '../../../contracts/permissions';
import {
  getOrder,
  shipOrder,
  updateOrderAddress,
  cancelOrder,
  addOrderNote,
  refundSubstitute,
  type OrderStatus,
} from '../../../api/orders';

const { Text, Title } = Typography;

/** 分转元，保留 2 位小数 */
function centToYuan(cents: number): string {
  return `¥${(cents / 100).toFixed(2)}`;
}

/** ISO → 本地时间字符串 */
function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '-';
  return new Date(iso).toLocaleString('zh-CN');
}

function orderStatusColor(status: OrderStatus): string {
  switch (status) {
    case 'pending_payment': return 'default';
    case 'paid':            return 'blue';
    case 'shipped':         return 'gold';
    case 'completed':       return 'green';
    case 'cancelled':       return 'red';
    default:                return 'default';
  }
}

function orderStatusLabel(status: OrderStatus): string {
  switch (status) {
    case 'pending_payment': return '待支付';
    case 'paid':            return '已支付';
    case 'shipped':         return '已发货';
    case 'completed':       return '已完成';
    case 'cancelled':       return '已取消';
    default:                return status;
  }
}

export default function OrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();

  // Modal visibility
  const [shipOpen, setShipOpen] = useState(false);
  const [addressOpen, setAddressOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [substituteOpen, setSubstituteOpen] = useState(false);
  const [refundOpen, setRefundOpen] = useState(false);

  // Forms
  const [shipForm] = Form.useForm<{ trackingNumber: string; company: string; shippedAt?: string }>();
  const [addressForm] = Form.useForm<{
    receiver_name?: string;
    receiver_phone?: string;
    receiver_province?: string;
    receiver_city?: string;
    receiver_district?: string;
    receiver_address?: string;
  }>();
  const [cancelForm] = Form.useForm<{ reason: string }>();
  const [noteForm] = Form.useForm<{ note: string }>();
  const [substituteForm] = Form.useForm<{ refundAmount: number; reason: string }>();

  const orderId = id ?? '';

  const detailQuery = useQuery({
    queryKey: ['order', orderId],
    queryFn: () => getOrder(orderId),
    enabled: !!orderId,
  });

  function invalidate() {
    qc.invalidateQueries({ queryKey: ['order', orderId] });
  }

  // --- Mutations ---
  const shipMutation = useMutation({
    mutationFn: (vals: { trackingNumber: string; company: string; shippedAt?: string }) =>
      shipOrder(orderId, vals),
    onSuccess: () => {
      message.success('运单已录入');
      setShipOpen(false);
      shipForm.resetFields();
      invalidate();
    },
    onError: (e: { response?: { data?: { message?: string } }; message?: string }) => {
      message.error(e.response?.data?.message ?? e.message ?? '操作失败');
    },
  });

  const addressMutation = useMutation({
    mutationFn: (vals: {
      receiver_name?: string;
      receiver_phone?: string;
      receiver_province?: string;
      receiver_city?: string;
      receiver_district?: string;
      receiver_address?: string;
    }) => updateOrderAddress(orderId, vals),
    onSuccess: () => {
      message.success('地址已更新');
      setAddressOpen(false);
      addressForm.resetFields();
      invalidate();
    },
    onError: (e: { response?: { data?: { message?: string } }; message?: string }) => {
      message.error(e.response?.data?.message ?? e.message ?? '操作失败');
    },
  });

  const cancelMutation = useMutation({
    mutationFn: (vals: { reason: string }) => cancelOrder(orderId, vals),
    onSuccess: () => {
      message.success('订单已取消');
      setCancelOpen(false);
      cancelForm.resetFields();
      invalidate();
    },
    onError: (e: { response?: { data?: { message?: string } }; message?: string }) => {
      message.error(e.response?.data?.message ?? e.message ?? '操作失败');
    },
  });

  const noteMutation = useMutation({
    mutationFn: (vals: { note: string }) => addOrderNote(orderId, vals),
    onSuccess: () => {
      message.success('备注已保存');
      setNoteOpen(false);
      noteForm.resetFields();
      invalidate();
    },
    onError: (e: { response?: { data?: { message?: string } }; message?: string }) => {
      message.error(e.response?.data?.message ?? e.message ?? '操作失败');
    },
  });

  const substituteMutation = useMutation({
    mutationFn: (vals: { refundAmount: number; reason: string }) =>
      refundSubstitute(orderId, { refundAmount: Math.round(vals.refundAmount * 100), reason: vals.reason }),
    onSuccess: () => {
      message.success('替支付反悔退款已提交');
      setSubstituteOpen(false);
      substituteForm.resetFields();
      invalidate();
    },
    onError: (e: { response?: { data?: { message?: string } }; message?: string }) => {
      message.error(e.response?.data?.message ?? e.message ?? '操作失败');
    },
  });

  // --- Handlers ---
  function submitShip() {
    shipForm.validateFields().then((vals) => shipMutation.mutate(vals));
  }

  function submitAddress() {
    addressForm.validateFields().then((vals) => addressMutation.mutate(vals));
  }

  function submitCancel() {
    cancelForm.validateFields().then((vals) => cancelMutation.mutate(vals));
  }

  function submitNote() {
    noteForm.validateFields().then((vals) => noteMutation.mutate(vals));
  }

  function submitSubstitute() {
    substituteForm.validateFields().then((vals) => substituteMutation.mutate(vals));
  }

  // --- Render states ---
  if (detailQuery.isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 60 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (detailQuery.isError || !detailQuery.data) {
    return (
      <Result
        status="error"
        title="加载失败"
        subTitle="订单详情获取失败，请刷新重试。"
        extra={
          <Button onClick={() => detailQuery.refetch()}>重试</Button>
        }
      />
    );
  }

  const { order, payment, logistics, work, isSubstitutePayment } = detailQuery.data;

  // Timeline items
  const timelineItems: { label: string; children: string; color?: string }[] = [
    { label: '创建', children: fmtDate(order.createdAt), color: 'blue' },
  ];
  if (order.paidAt) {
    timelineItems.push({ label: '已支付', children: fmtDate(order.paidAt), color: 'green' });
  }
  if (order.status !== 'cancelled' && order.shippedAt) {
    timelineItems.push({ label: '已发货', children: fmtDate(order.shippedAt), color: 'gold' });
  }
  if (order.cancelledAt) {
    timelineItems.push({ label: '已取消', children: fmtDate(order.cancelledAt), color: 'red' });
  }

  return (
    <div data-testid="order-detail-page" style={{ padding: '0 0 40px' }}>
      {/* 顶部 Header */}
      <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
        <Link to="/orders">
          <Button>← 返回列表</Button>
        </Link>
        <Title level={4} style={{ margin: 0 }}>
          订单 {order.orderId}
        </Title>
        <Tag color={orderStatusColor(order.status)}>
          {orderStatusLabel(order.status)}
        </Tag>
      </div>

      <Space direction="vertical" size="middle" style={{ width: '100%' }}>

        {/* 区块 1：基础信息 */}
        <Card title="基础信息" size="small">
          <Descriptions column={2} size="small">
            <Descriptions.Item label="订单号">{order.orderId}</Descriptions.Item>
            <Descriptions.Item label="用户 ID">{order.userId}</Descriptions.Item>
            <Descriptions.Item label="作品 ID">{order.workId}</Descriptions.Item>
            <Descriptions.Item label="总金额">{centToYuan(order.totalAmount)}</Descriptions.Item>
            <Descriptions.Item label="订单状态">
              <Tag color={orderStatusColor(order.status)}>{orderStatusLabel(order.status)}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="支付状态">
              <Tag>{order.paymentStatus}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="物流状态">
              <Tag>{order.logisticsStatus}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="创建时间">{fmtDate(order.createdAt)}</Descriptions.Item>
            <Descriptions.Item label="更新时间">{fmtDate(order.updatedAt)}</Descriptions.Item>
          </Descriptions>
        </Card>

        {/* 区块 2：状态时间线 */}
        <Card title="状态时间线" size="small">
          <Timeline
            items={timelineItems.map((item) => ({
              color: item.color,
              children: (
                <span>
                  <Text strong>{item.label}</Text>
                  <Text type="secondary" style={{ marginLeft: 8 }}>
                    {item.children}
                  </Text>
                </span>
              ),
            }))}
          />
        </Card>

        {/* 区块 3：收件人信息 */}
        <Card title="收件人信息" size="small">
          <Descriptions column={2} size="small">
            <Descriptions.Item label="姓名">{order.receiverName}</Descriptions.Item>
            <Descriptions.Item label="手机">{order.receiverPhone}</Descriptions.Item>
            <Descriptions.Item label="省">{order.receiverProvince}</Descriptions.Item>
            <Descriptions.Item label="市">{order.receiverCity}</Descriptions.Item>
            <Descriptions.Item label="区/县">{order.receiverDistrict}</Descriptions.Item>
            <Descriptions.Item label="详细地址">{order.receiverAddress}</Descriptions.Item>
          </Descriptions>
        </Card>

        {/* 区块 4：物流信息 */}
        {logistics !== null && (
          <Card title="物流信息" size="small">
            <Descriptions column={2} size="small">
              <Descriptions.Item label="物流记录 ID">{logistics.logisticsRecordId}</Descriptions.Item>
              <Descriptions.Item label="状态">{logistics.status}</Descriptions.Item>
              <Descriptions.Item label="快递公司">{logistics.company ?? '-'}</Descriptions.Item>
              <Descriptions.Item label="运单号">{logistics.trackingNumber ?? '-'}</Descriptions.Item>
              <Descriptions.Item label="发货时间">{fmtDate(logistics.shippedAt)}</Descriptions.Item>
              <Descriptions.Item label="物流轨迹">
                {logistics.trackingEvents
                  ? JSON.stringify(logistics.trackingEvents)
                  : '-'}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        )}

        {/* 区块 5：支付信息 */}
        {payment !== null && (
          <Card
            title="支付信息"
            size="small"
            extra={
              <PermissionGate point={PERMISSIONS.REFUND_INITIATE}>
                <Button
                  size="small"
                  type="primary"
                  ghost
                  onClick={() => setRefundOpen(true)}
                  data-testid="initiate-order-refund-btn"
                >
                  发起退款
                </Button>
              </PermissionGate>
            }
          >
            <Descriptions column={2} size="small">
              <Descriptions.Item label="支付记录 ID">
                <Text copyable={{ text: payment.paymentRecordId }}>
                  {payment.paymentRecordId}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="付款人 ID">{payment.payerUserId}</Descriptions.Item>
              <Descriptions.Item label="支付金额">{centToYuan(payment.amount)}</Descriptions.Item>
              <Descriptions.Item label="支付方式">{payment.paymentMethod}</Descriptions.Item>
              <Descriptions.Item label="支付状态">{payment.status}</Descriptions.Item>
              <Descriptions.Item label="支付时间">{fmtDate(payment.paidAt)}</Descriptions.Item>
              <Descriptions.Item label="退款状态">{payment.refundStatus ?? '-'}</Descriptions.Item>
              <Descriptions.Item label="退款金额">
                {payment.refundAmount != null ? centToYuan(payment.refundAmount) : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="退款时间">{fmtDate(payment.refundedAt)}</Descriptions.Item>
            </Descriptions>
          </Card>
        )}

        {/* 区块 6：关联作品 */}
        {work !== null && (
          <Card title="关联作品" size="small">
            <Descriptions column={2} size="small">
              <Descriptions.Item label="作品 ID">{work.workId}</Descriptions.Item>
              <Descriptions.Item label="标题">{work.title}</Descriptions.Item>
              <Descriptions.Item label="所有者 ID">{work.ownerUserId}</Descriptions.Item>
              <Descriptions.Item label="书籍已解锁">
                {work.isBookUnlocked ? (
                  <Tag color="green">是</Tag>
                ) : (
                  <Tag color="red">否</Tag>
                )}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        )}

        {/* 区块 7：替支付反悔模块（仅 isSubstitutePayment=true） */}
        {isSubstitutePayment && (
          <Card
            title={
              <Text style={{ color: '#cf1322' }}>
                ⚠ 替支付反悔退款
              </Text>
            }
            headStyle={{ background: '#fff1f0', borderBottom: '1px solid #ffa39e' }}
            style={{ border: '1px solid #ffa39e' }}
            size="small"
            data-testid="substitute-payment-card"
          >
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message={
                payment && work
                  ? `本订单为替支付订单：付款人 ${payment.payerUserId} ≠ 作品所有者 ${work.ownerUserId}。点击下方按钮发起反悔退款，将同时退款给付款人 + 撤销 work.isBookUnlocked = false。`
                  : '本订单为替支付订单，可发起反悔退款。'
              }
            />
            <PermissionGate point={PERMISSIONS.ORDER_REVERT_SUBSTITUTE}>
              <Button
                type="primary"
                danger
                onClick={() => setSubstituteOpen(true)}
                data-testid="substitute-refund-btn"
              >
                替支付反悔退款
              </Button>
            </PermissionGate>
          </Card>
        )}

        {/* 区块 8：管理员备注 */}
        <Card title="管理员备注" size="small">
          <div style={{ marginBottom: 8 }}>
            {order.adminNote ? (
              <Text>{order.adminNote}</Text>
            ) : (
              <Text type="secondary">（暂无备注）</Text>
            )}
          </div>
          <PermissionGate point={PERMISSIONS.ORDER_UPDATE_LOGISTICS}>
            <Button
              size="small"
              onClick={() => {
                noteForm.setFieldsValue({ note: order.adminNote ?? '' });
                setNoteOpen(true);
              }}
              data-testid="edit-note-btn"
            >
              编辑备注
            </Button>
          </PermissionGate>
        </Card>

        {/* 区块 9：操作面板 */}
        <Card title="操作面板" size="small">
          <Space wrap>
            <PermissionGate point={PERMISSIONS.ORDER_UPDATE_LOGISTICS}>
              <Button
                onClick={() => setShipOpen(true)}
                data-testid="ship-order-btn"
              >
                录入运单
              </Button>
            </PermissionGate>

            <PermissionGate point={PERMISSIONS.ORDER_UPDATE_LOGISTICS}>
              <Button
                onClick={() => {
                  addressForm.setFieldsValue({
                    receiver_name: order.receiverName,
                    receiver_phone: order.receiverPhone,
                    receiver_province: order.receiverProvince,
                    receiver_city: order.receiverCity,
                    receiver_district: order.receiverDistrict,
                    receiver_address: order.receiverAddress,
                  });
                  setAddressOpen(true);
                }}
                data-testid="update-address-btn"
              >
                修改地址
              </Button>
            </PermissionGate>

            {order.status === 'pending_payment' && (
              <PermissionGate point={PERMISSIONS.ORDER_UPDATE_LOGISTICS}>
                <Button
                  danger
                  onClick={() => setCancelOpen(true)}
                  data-testid="cancel-order-btn"
                >
                  取消订单
                </Button>
              </PermissionGate>
            )}
          </Space>
        </Card>
      </Space>

      {/* Modal：录入运单 */}
      <Modal
        title="录入运单"
        open={shipOpen}
        onCancel={() => setShipOpen(false)}
        onOk={submitShip}
        confirmLoading={shipMutation.isPending}
        data-testid="ship-modal"
      >
        <Form form={shipForm} layout="vertical">
          <Form.Item
            name="trackingNumber"
            label="运单号"
            rules={[{ required: true, message: '必填' }]}
          >
            <Input placeholder="请输入运单号" />
          </Form.Item>
          <Form.Item
            name="company"
            label="快递公司"
            rules={[{ required: true, message: '必填' }]}
          >
            <Input placeholder="例：顺丰 / 圆通 / 中通" />
          </Form.Item>
          <Form.Item name="shippedAt" label="发货时间（可选）">
            <Input placeholder="ISO 格式，留空则由后端取当前时间" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal：修改地址 */}
      <Modal
        title="修改收件地址"
        open={addressOpen}
        onCancel={() => setAddressOpen(false)}
        onOk={submitAddress}
        confirmLoading={addressMutation.isPending}
        data-testid="address-modal"
      >
        <Form form={addressForm} layout="vertical">
          <Form.Item name="receiver_name" label="收件人姓名">
            <Input />
          </Form.Item>
          <Form.Item name="receiver_phone" label="手机号">
            <Input />
          </Form.Item>
          <Form.Item name="receiver_province" label="省">
            <Input />
          </Form.Item>
          <Form.Item name="receiver_city" label="市">
            <Input />
          </Form.Item>
          <Form.Item name="receiver_district" label="区/县">
            <Input />
          </Form.Item>
          <Form.Item name="receiver_address" label="详细地址">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal：取消订单 */}
      <Modal
        title="取消订单"
        open={cancelOpen}
        onCancel={() => setCancelOpen(false)}
        onOk={submitCancel}
        confirmLoading={cancelMutation.isPending}
        okButtonProps={{ danger: true }}
        data-testid="cancel-modal"
      >
        <p style={{ color: '#cf1322', marginBottom: 8 }}>
          取消订单后不可恢复，请谨慎操作。
        </p>
        <Form form={cancelForm} layout="vertical">
          <Form.Item
            name="reason"
            label="取消原因（必填，将记入审计日志）"
            rules={[{ required: true, message: '必填' }]}
          >
            <Input.TextArea rows={3} placeholder="例：用户主动申请 / 超时未支付" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal：编辑备注 */}
      <Modal
        title="编辑管理员备注"
        open={noteOpen}
        onCancel={() => setNoteOpen(false)}
        onOk={submitNote}
        confirmLoading={noteMutation.isPending}
        data-testid="note-modal"
      >
        <Form form={noteForm} layout="vertical">
          <Form.Item
            name="note"
            label="备注内容"
            rules={[{ required: true, message: '必填' }]}
          >
            <Input.TextArea rows={4} placeholder="内部备注，不对用户可见" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal：替支付反悔退款 */}
      <Modal
        title="替支付反悔退款"
        open={substituteOpen}
        onCancel={() => setSubstituteOpen(false)}
        onOk={submitSubstitute}
        confirmLoading={substituteMutation.isPending}
        okButtonProps={{ danger: true }}
        okText="确认退款"
        data-testid="substitute-modal"
      >
        <Alert
          type="warning"
          showIcon
          message="退款将原路退回付款人微信账户，预计 7-15 个工作日到账。"
          style={{ marginBottom: 16 }}
        />
        <Form form={substituteForm} layout="vertical">
          <Form.Item
            name="refundAmount"
            label="退款金额（元）"
            rules={[
              { required: true, message: '必填' },
              { type: 'number', min: 0.01, message: '退款金额必须大于 0' },
            ]}
          >
            <InputNumber
              min={0.01}
              precision={2}
              style={{ width: '100%' }}
              placeholder="单位：元"
              data-testid="substitute-amount-input"
            />
          </Form.Item>
          <Form.Item
            name="reason"
            label="退款原因（必填，将记入审计日志）"
            rules={[{ required: true, message: '必填' }]}
          >
            <Input.TextArea
              rows={3}
              placeholder="例：付款人非作品所有者，发起反悔退款"
              data-testid="substitute-reason-input"
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal：发起退款（双人审批流；预填支付记录 ID / 金额 / 订单 / 作品） */}
      <InitiateRefundModal
        open={refundOpen}
        onClose={() => setRefundOpen(false)}
        onSuccess={invalidate}
        prefill={{
          paymentRecordId: payment?.paymentRecordId,
          refundAmountYuan: payment ? payment.amount / 100 : undefined,
          orderId: order.orderId,
          workId: work?.workId,
          lockPaymentRecordId: true,
        }}
      />
    </div>
  );
}
